import React, { useState, useEffect, useRef } from 'react';
import rentalApi, { CreateRentPinRequest } from '../services/api';
import { GTA_CITIES } from '../types';
import { AddressAutocomplete } from './AddressAutocomplete';
import './FormShared.css';

const PROP_TYPES: { label: string; icon: string }[] = [
  { label: 'Basement',     icon: '🏚' },
  { label: 'Apartment',    icon: '🏢' },
  { label: 'Condo',        icon: '🏙' },
  { label: 'Townhouse',    icon: '🏘' },
  { label: 'Semi-Detached',icon: '🏠' },
  { label: 'Detached',     icon: '🏡' },
];
const BHK_OPTS  = ['1', '1+', '2', '2+', '3+'];
const BATH_OPTS = ['1', '1+', '2', '2+', '3+'];
const FURNISH   = ['Furnished', 'Semi-Furnished', 'Unfurnished'];
const RESIDENTS = ['Bachelor', 'Family', 'Mixed'];
const UTILITIES = ['Hydro', 'Internet', 'Water'];
const AMENITIES = ['🏊 Pool', '🏋 Gym', '🚗 Parking', '🧖 Sauna'];
const PETS      = ['Yes', 'No', 'Not Sure'];
const TRANSIT   = ['0–5 km', '5–10 km', '10+ km'];

interface Props {
  onSuccess: () => void;
  onCancel: () => void;
  externalCoords?: [number, number];
  onCoordinatesChange?: (c: [number, number]) => void;
  onOpenPrivacy?: () => void;
  onOpenTerms?: () => void;
}

export const PinRentForm: React.FC<Props> = ({ onSuccess, onCancel, externalCoords, onCoordinatesChange, onOpenPrivacy, onOpenTerms }) => {
  const [address, setAddress]         = useState('');
  const [coords, setCoords]           = useState<[number, number] | null>(null);
  const [city, setCity]               = useState('Toronto');
  const [postal, setPostal]           = useState('');
  const [rent, setRent]               = useState('');
  const [propType, setPropType]       = useState('');
  const [bhk, setBhk]                 = useState<number | string | null>(null);
  const [baths, setBaths]             = useState('');
  const [furnishing, setFurnishing]   = useState('');
  const [residents, setResidents]     = useState('');
  const [sqft, setSqft]               = useState('');
  const [utilities, setUtilities]     = useState<string[]>([]);
  const [amenities, setAmenities]     = useState<string[]>([]);
  const [pets, setPets]               = useState('');
  const [transit, setTransit]         = useState('');
  const [schoolRating, setSchoolRating] = useState(0);
  const [description, setDescription] = useState('');
  const [anonymous, setAnonymous]     = useState(true);
  const [email, setEmail]             = useState('');
  const [phone, setPhone]             = useState('');
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [consentAt, setConsentAt]     = useState<string | null>(null);
  const [submitted, setSubmitted]     = useState<{ link: string } | null>(null);
  const [copied, setCopied]           = useState(false);

  const coordsFromAutocompleteRef = useRef('');

  useEffect(() => {
    if (!externalCoords) return;
    const [lng, lat] = externalCoords;
    setCoords(externalCoords);
    const key = `${lng.toFixed(6)},${lat.toFixed(6)}`;
    if (coordsFromAutocompleteRef.current === key) return;
    if (!window.google?.maps) return;
    new window.google.maps.Geocoder().geocode({ location: { lat, lng } }, (results, status) => {
      if (status === 'OK' && results?.[0]) {
        const r = results[0];
        setAddress(r.formatted_address);
        const comps = r.address_components ?? [];
        const cityComp = comps.find(c => c.types.includes('locality'));
        const postalComp = comps.find(c => c.types.includes('postal_code'));
        if (cityComp) setCity(cityComp.long_name);
        if (postalComp) setPostal(postalComp.short_name);
      }
    });
  }, [externalCoords]);

  const toggleChip = (setArr: React.Dispatch<React.SetStateAction<string[]>>, val: string) =>
    setArr(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]);

  // Progress: count filled required sections
  const sectionsComplete = [
    !!address && !!coords,
    !!rent && parseFloat(rent) > 0,
    true, // living details always visible
    true, // neighbourhood always visible
    true, // anything else always visible
  ].filter(Boolean).length;

  const handleSubmit = async () => {
    setError('');
    if (!address) return setError('Please search and select an address');
    if (!rent || parseFloat(rent) <= 0) return setError('Monthly rent is required');
    if (parseFloat(rent) > 9999) return setError('Monthly rent cannot exceed $9,999');
    if (!coords) return setError('No location set — search an address or click the map');
    const [lng, lat] = coords;
    if (lat < 43.1 || lat > 44.5 || lng < -80.7 || lng > -78.4) return setError("Sorry, we don't operate in this zone yet.");
    if (city && !GTA_CITIES.includes(city)) return setError("Sorry, we don't operate in this zone yet.");
    if (!propType) return setError('Select a Property Type');
    if (bhk === null) return setError('Select the number of Bedrooms');
    if (!termsAccepted) return setError('Please accept the Terms of Use and Privacy Policy to continue');
    setLoading(true);
    try {
      const payload: CreateRentPinRequest = {
        location: { coordinates: coords },
        address,
        city: city || 'Toronto',
        postalCode: postal || undefined,
        bedrooms: bhk === null ? undefined : parseInt(String(bhk)),
        monthlyRent: parseFloat(rent),
        furnishing: furnishing ? furnishing as any : undefined,
        residentsType: residents ? residents as any : undefined,
        propertyType: propType || undefined,
        baths,
        amenities: amenities.length ? amenities : undefined,
        utilitiesIncluded: utilities.length ? utilities : undefined,
        petsAllowedStatus: pets,
        transitDistance: transit || undefined,
        schoolRating: schoolRating || undefined,
        description: description || undefined,
        squareFootage: sqft ? parseFloat(sqft) : undefined,
        anonymousSubmission: anonymous,
        email: !anonymous ? email || undefined : undefined,
        phone: !anonymous ? phone || undefined : undefined,
        consentAcceptedAt: consentAt ?? undefined,
      };
      const res = await rentalApi.createRentPin(payload);
      const { id } = res.data.data;
      const token = res.data.deleteToken;
      if (token) localStorage.setItem(`pin-token-${id}`, token);
      const link = token
        ? `${window.location.origin}/?pinId=${id}&deleteToken=${token}`
        : '';
      setSubmitted({ link });
    } catch (err: any) {
      const msg: string = err.message || '';
      setError(msg || 'Failed to pin rent. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const Tgl = ({ opts, val, onSet, labelId }: { opts: (string | number)[], val: any, onSet: (v: any) => void; labelId: string }) => (
    <div className="toggle-group" role="group" aria-labelledby={labelId}>
      {opts.map(o => (
        <button type="button" key={String(o)}
          className={`tgl${val === o ? ' on' : ''}`}
          aria-pressed={val === o}
          onClick={() => onSet(o)}>
          {o}
        </button>
      ))}
    </div>
  );

  const Chips = ({ opts, sel, setSel, labelId }: { opts: string[], sel: string[], setSel: React.Dispatch<React.SetStateAction<string[]>>; labelId: string }) => (
    <div className="chip-group" role="group" aria-labelledby={labelId}>
      {opts.map(o => (
        <button type="button" key={o}
          className={`chip${sel.includes(o) ? ' on' : ''}`}
          aria-pressed={sel.includes(o)}
          onClick={() => toggleChip(setSel, o)}>
          {o}
        </button>
      ))}
    </div>
  );

  if (submitted) {
    const copyLink = () => {
      navigator.clipboard.writeText(submitted.link).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    };
    return (
      <div className="form-panel">
        <div className="form-success">
          <div className="form-success-icon">✓</div>
          <h3>Pin added!</h3>
          <p>Save this link to edit or remove your pin later.</p>
          {submitted.link && (
            <>
              <div className="form-success-link-row">
                <input className="form-success-link-input" readOnly value={submitted.link} />
                <button className={`form-success-copy${copied ? ' copied' : ''}`} onClick={copyLink}>
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <p className="form-success-note">Anyone with this link can delete this pin. Keep it safe.</p>
            </>
          )}
          <button className="form-success-done" onClick={onSuccess}>Done</button>
        </div>
      </div>
    );
  }

  return (
    <div className="form-panel" role="form" aria-label="Pin a rental">
      {/* ── Header ── */}
      <div className="form-panel-header">
        <div className="form-panel-eyebrow">
          <span>Pin a Rental</span>
          <button type="button" className="form-panel-close" onClick={onCancel} aria-label="Close form"><span aria-hidden="true">✕</span></button>
        </div>
        <h2 className="form-panel-title">Help others find their next home</h2>
        <p className="form-panel-sub">Drop a pin on a rental you know about. Takes ~2 minutes.</p>
      </div>

      {/* ── Body ── */}
      <div className="form-panel-body">

        {/* 01 Where is it? */}
        <div className="form-section">
          <div className="form-section-head" aria-hidden="true">
            <span className="form-section-num">01</span>
            <span className="form-section-title">Where is it?</span>
          </div>
          <div className="fs">
            <label htmlFor="prf-address" className="fs-label">Address <span aria-hidden="true" className="fs-label-req">*</span><span className="sr-only">(required)</span></label>
            <AddressAutocomplete
              inputId="prf-address"
              value={address}
              onChange={setAddress}
              onPlaceSelect={(addr, c, ct, pc) => {
                coordsFromAutocompleteRef.current = `${c[0].toFixed(6)},${c[1].toFixed(6)}`;
                setAddress(addr);
                setCoords(c);
                if (ct) setCity(ct);
                if (pc) setPostal(pc);
                onCoordinatesChange?.(c);
              }}
              onClear={() => { setAddress(''); setCoords(null); }}
              placeholder="Search address…"
              aria-required="true"
            />
            {coords && address && (
              <div className="addr-confirm" role="status">
                <span className="addr-confirm-dot" aria-hidden="true" />
                Pin will be placed at {address.split(',').slice(0, 2).join(',')}
              </div>
            )}
          </div>
        </div>

        {/* 02 The basics */}
        <div className="form-section">
          <div className="form-section-head" aria-hidden="true">
            <span className="form-section-num">02</span>
            <span className="form-section-title">The basics</span>
          </div>

          <div className="fs">
            <label htmlFor="prf-rent" className="fs-label">Monthly Rent (CAD) <span aria-hidden="true" className="fs-label-req">*</span><span className="sr-only">(required)</span></label>
            <div className="rent-input-row">
              <span className="rent-prefix" aria-hidden="true">$</span>
              <input id="prf-rent" className="rent-input" type="number" value={rent}
                onChange={e => setRent(e.target.value)} placeholder="e.g. 2200" min="1" max="9999"
                aria-required="true" />
              <span className="rent-suffix" aria-hidden="true">/mo</span>
            </div>
          </div>
          <div className="fs">
            <label htmlFor="prf-sqft" className="fs-label">Sq Ft <span className="fs-label-opt">optional</span></label>
            <input id="prf-sqft" className="fi" type="number" value={sqft}
              onChange={e => setSqft(e.target.value)} placeholder="e.g. 650" min="0" />
          </div>

          <div className="fs">
            <span id="prf-proptype-label" className="fs-label">Property Type <span aria-hidden="true" className="fs-label-req">*</span><span className="sr-only">(required)</span></span>
            <div className="proptype-grid" role="group" aria-labelledby="prf-proptype-label">
              {PROP_TYPES.map(pt => (
                <button type="button" key={pt.label}
                  className={`proptype-tile${propType === pt.label ? ' on' : ''}`}
                  aria-pressed={propType === pt.label}
                  onClick={() => setPropType(propType === pt.label ? '' : pt.label)}>
                  <span className="proptype-tile-icon" aria-hidden="true">{pt.icon}</span>
                  <span>{pt.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="fs">
            <span id="prf-bhk-label" className="fs-label">Bedrooms <span aria-hidden="true" className="fs-label-req">*</span><span className="sr-only">(required)</span></span>
            <div className="bhk-group" role="group" aria-labelledby="prf-bhk-label">
              {BHK_OPTS.map(o => (
                <button type="button" key={String(o)}
                  className={`bhk-tile${bhk === o ? ' on' : ''}`}
                  aria-pressed={bhk === o}
                  onClick={() => setBhk(o)}>
                  {o}
                </button>
              ))}
            </div>
          </div>
          <div className="fs">
            <span id="prf-baths-label" className="fs-label">Baths</span>
            <div className="bhk-group" role="group" aria-labelledby="prf-baths-label">
              {BATH_OPTS.map(o => (
                <button type="button" key={String(o)}
                  className={`bhk-tile${baths === o ? ' on' : ''}`}
                  aria-pressed={baths === o}
                  onClick={() => setBaths(o)}>
                  {o}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* 03 Living details */}
        <div className="form-section">
          <div className="form-section-head" aria-hidden="true">
            <span className="form-section-num">03</span>
            <span className="form-section-title">Living details</span>
          </div>

          <div className="fs">
            <span id="prf-furnish-label" className="fs-label">Furnishing</span>
            <Tgl opts={FURNISH} val={furnishing} onSet={setFurnishing} labelId="prf-furnish-label" />
          </div>

          <div className="fs">
            <span id="prf-residents-label" className="fs-label">Residents Type</span>
            <Tgl opts={RESIDENTS} val={residents} onSet={setResidents} labelId="prf-residents-label" />
          </div>

          <div className="fs">
            <span id="prf-utilities-label" className="fs-label">Utilities Included</span>
            <Chips opts={UTILITIES} sel={utilities} setSel={setUtilities} labelId="prf-utilities-label" />
          </div>

          <div className="fs">
            <span id="prf-amenities-label" className="fs-label">Amenities</span>
            <Chips opts={AMENITIES} sel={amenities} setSel={setAmenities} labelId="prf-amenities-label" />
          </div>

          <div className="fs">
            <span id="prf-pets-label" className="fs-label">Pets Allowed</span>
            <Tgl opts={PETS} val={pets} onSet={setPets} labelId="prf-pets-label" />
          </div>
        </div>

        {/* 04 Neighbourhood */}
        <div className="form-section">
          <div className="form-section-head" aria-hidden="true">
            <span className="form-section-num">04</span>
            <span className="form-section-title">Neighbourhood</span>
          </div>

          <div className="fs">
            <span id="prf-transit-label" className="fs-label">Transit Access</span>
            <Tgl opts={TRANSIT} val={transit} onSet={v => setTransit(transit === v ? '' : v)} labelId="prf-transit-label" />
          </div>

          <div className="fs">
            <label htmlFor="prf-school-input" className="fs-label">School Rating</label>
            <div className="school-rating-row">
              <input
                type="range"
                id="prf-school-input"
                className="school-rating-slider"
                min={0} max={10} step={1}
                value={schoolRating}
                onChange={e => setSchoolRating(Number(e.target.value))}
                aria-valuetext={schoolRating === 0 ? 'Unknown' : `${schoolRating} out of 10`}
              />
              <span className="school-rating-badge" aria-live="polite">
                {schoolRating === 0 ? 'Unknown' : `${schoolRating}/10`}
              </span>
            </div>
            <div className="school-rating-scale">
              <span>0 · Unknown</span><span>10 · Excellent</span>
            </div>
          </div>
        </div>

        {/* 05 Anything else? */}
        <div className="form-section">
          <div className="form-section-head" aria-hidden="true">
            <span className="form-section-num">05</span>
            <span className="form-section-title">Anything else?</span>
          </div>

          <div className="fs">
            <label htmlFor="prf-desc" className="fs-label">One-liner <span className="fs-label-opt">optional · 200 chars</span></label>
            <textarea id="prf-desc" className="fi" rows={2} value={description}
              onChange={e => setDescription(e.target.value.slice(0, 200))}
              placeholder="e.g. Bright corner unit near subway, updated kitchen…"
              aria-describedby="prf-desc-cc" />
            <span id="prf-desc-cc" className="cc" aria-live="polite">{description.length}/200</span>
          </div>

          <div className="anon-block">
            <div className="anon-row">
              <button
                type="button"
                className={`anon-toggle${anonymous ? ' on' : ''}`}
                aria-pressed={anonymous}
                aria-label={anonymous ? 'Submitting anonymously — click to add contact info' : 'Not anonymous — click to submit anonymously'}
                onClick={() => setAnonymous(a => !a)}
              />
              <div aria-hidden="true">
                <div className="anon-label">Submit Anonymously</div>
                <div className="anon-sub">Your contact info won't appear publicly</div>
              </div>
            </div>
            {!anonymous && (
              <div className="g2">
                <div className="fs">
                  <label htmlFor="prf-email" className="fs-label">Email</label>
                  <input id="prf-email" className="fi" type="email" value={email}
                    onChange={e => setEmail(e.target.value)} placeholder="Optional" />
                </div>
                <div className="fs">
                  <label htmlFor="prf-phone" className="fs-label">Phone</label>
                  <input id="prf-phone" className="fi" type="tel" value={phone}
                    onChange={e => setPhone(e.target.value)} placeholder="Optional" />
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="consent-box">
          <label className="consent-label">
            <input
              type="checkbox"
              className="consent-check"
              checked={termsAccepted}
              onChange={e => {
                setTermsAccepted(e.target.checked);
                setConsentAt(e.target.checked ? new Date().toISOString() : null);
              }}
              aria-required="true"
            />
            <span className="consent-text">
              I agree to the{' '}
              <button type="button" className="consent-link" onClick={() => onOpenTerms?.()}>Terms of Use</button>
              {' '}and{' '}
              <button type="button" className="consent-link" onClick={() => onOpenPrivacy?.()}>Privacy Policy</button>
              . I understand my email is used only to manage this listing and will be deleted after 30 days of inactivity.
            </span>
          </label>
        </div>
      </div>

      {/* ── Footer ── */}
      <div className="form-actions">
        {error && <div className="err-footer" role="alert">{error}</div>}
        <span className="form-progress" aria-live="polite">{sectionsComplete}/5 sections · {coords ? 'ready to pin' : 'set a location'}</span>
        <button type="button" className="btn-cancel" onClick={onCancel} disabled={loading}>Cancel</button>
        <button type="button" className="btn-submit" onClick={handleSubmit} disabled={loading} aria-busy={loading}>
          {loading ? 'Pinning…' : '📍 Pin It'}
        </button>
      </div>
    </div>
  );
};
